<template>
  <RevForm
    :initial-values="initialValues"
    :validate="validate"
    @submit="handleSubmit"
  >
    <template #default="{ values, errors }">
      <div class="space-y-32">
        <div class="space-y-16">
          <h3 class="heading-3">{{ i18n(translations.setUpYourLineLabel) }}</h3>
          <RevCard class="space-y-24 p-24">
            <div class="space-y-16">
              <div class="grid grid-cols-1 gap-16 md:grid-cols-2 md:flex-row">
                <RevRadio
                  v-model="values.option"
                  name="option"
                  value="KEEP_PHONE_NUMBER"
                  variant="full"
                >
                  <template #label>
                    {{ i18n(translations.keepPhoneNumberLabel) }}
                  </template>

                  <template #description>
                    <PriceOption :prices="keepPhoneNumberPriceOption.prices" />
                  </template>
                </RevRadio>

                <RevRadio
                  v-model="values.option"
                  name="option"
                  value="NEW_PHONE_NUMBER"
                  variant="full"
                >
                  <template #label>
                    {{ i18n(translations.newPhoneNumberLabel) }}
                  </template>

                  <template #description>
                    <PriceOption :prices="newPhoneNumberPriceOption.prices" />
                  </template>
                </RevRadio>
              </div>

              <p class="caption">
                {{ i18n(translations.simCardShippingInfo) }}
              </p>
            </div>

            <div
              v-if="values.option === 'KEEP_PHONE_NUMBER'"
              class="space-y-16"
            >
              <header>
                <div class="body-1-bold">
                  {{ i18n(translations.phoneNumberTitle) }}
                </div>
                <div class="caption">
                  {{ i18n(translations.phoneNumberSubtitle) }}
                </div>
              </header>

              <RevInputPhone
                id="phoneline-setup-phone-number"
                v-model="values.phoneNumber"
                :clear-button-aria-label="
                  i18n(translations.phoneNumberInputClear)
                "
                :default-country="MarketCountryCode.FR"
                description=""
                :error="errors.phoneNumber || apiErrors.phoneNumber"
                :label="i18n(translations.phoneNumberInputLabel)"
                :translate="i18n.country"
              />

              <RevInputText
                id="phoneline-setup-rio"
                v-model="values.rioCode"
                :description="i18n(translations.RIODescription)"
                :error="errors.rioCode || apiErrors.rioCode"
                :label="i18n(translations.RIO)"
              />
              <RevInfoBlock
                :content="i18n(translations.RIOInfoBlockContent)"
                :icon="IconSim"
                :title="i18n(translations.RIOInfoBlockTitle)"
                variant="default"
              >
                <RevLink class="body-2-link" to="tel:3179">{{
                  i18n(translations.RIOInfoBlockLink)
                }}</RevLink>
              </RevInfoBlock>
            </div>
          </RevCard>
        </div>

        <div class="space-y-16">
          <h3 class="heading-3">
            {{ i18n(translations.paymentInformationLabel) }}
          </h3>
          <RevCard class="p-24" space-y-8>
            <RevInputText
              id="phoneline-setup-iban"
              v-model="values.iban"
              :description="i18n(translations.IBANDescription)"
              :error="errors.iban || apiErrors.iban"
              :icon="IconBank"
              :label="i18n(translations.IBAN)"
            />
          </RevCard>
        </div>

        <div class="space-y-16">
          <h3 class="heading-3">{{ i18n(translations.moreOptionsLabel) }}</h3>
          <RevCard class="flex flex-col items-end space-y-8 p-24">
            <RevCheckbox
              v-model="values.childrenOnlineProtection"
              class="w-full"
              :image="IconLockLocked"
              :image-props="{
                alt: i18n(translations.parentalControlIconAlternativeText),
              }"
              variant="full"
            >
              <template #label>
                {{ i18n(translations.parentalControl) }}
              </template>
              <template #description>
                {{ i18n(translations.parentalControlInformation) }}
              </template>
            </RevCheckbox>
            <RevLink
              target="_blank"
              to="https://www.bouyguestelecom.fr/choisir-bouygues-telecom/protection-des-mineurs"
              >{{ i18n(translations.parentalControlLink) }}</RevLink
            >
          </RevCard>
        </div>

        <TermsAndConditionsCheckbox v-model="values.termsAndConditions" />

        <p v-if="errors.termsAndConditions" class="text-static-danger-hi">
          {{ errors.termsAndConditions }}
        </p>

        <div class="flex justify-end">
          <RevButton
            :disabled="isLoading"
            full-width="responsive"
            :loading="isLoading"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.submit) }}
          </RevButton>
        </div>

        <ReassuranceItems>
          <div class="flex items-center">
            <div
              class="bg-static-info-mid border-static-default-low rounded-lg flex items-center border p-8"
            >
              <IconSim />
            </div>
            <RevLink
              class="ml-12 text-left"
              @click="openModal('bouygues-assistance')"
            >
              {{ i18n(translations.bouyguesAssistance) }}
            </RevLink>
          </div>
        </ReassuranceItems>
      </div>
    </template>
  </RevForm>

  <FormattedMessage
    class="text-static-default-low caption block whitespace-pre-line"
    :definition="translations.returnPolicy"
  >
    <template #callPrice>
      <RevLink
        target="_blank"
        to="https://www.bouyguestelecom.fr/static/cms/tarifs/guide-des-tarifs-gp-octobre-2023.pdf"
      >
        {{ i18n(translations.callPrice) }}
      </RevLink>
    </template>

    <template #withdrawalForm>
      <RevLink
        target="_blank"
        to="https://www.bouyguestelecom.fr/static/cms/tarifs/Formulaire_De_Retractation.pdf"
      >
        {{ i18n(translations.withdrawalForm) }}
      </RevLink>
    </template>
  </FormattedMessage>

  <BouyguesAssistanceDrawer name="bouygues-assistance" />
</template>

<script setup lang="ts">
import { useRouter } from '#imports'
import { ref } from 'vue'

import { type HttpApiError, MarketCountryCode } from '@backmarket/http-api'
import { checkMobilePlanCustomerAccountEligibility } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan'
import type { PriceOption as PriceOptionType } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import useTheToast from '@backmarket/nuxt-layer-base/composables/useTheToast'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevCheckbox } from '@ds/components/Checkbox'
import {
  FORM_VALID,
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputPhone } from '@ds/components/InputPhone'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevRadio } from '@ds/components/Radio'
import { IconBank } from '@ds/icons/IconBank'
import { IconLockLocked } from '@ds/icons/IconLockLocked'
import { IconSim } from '@ds/icons/IconSim'

import { useUser } from '~/scopes/checkout/composables/useUser'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { useBouyguesStore } from '~/scopes/checkout/stores/bouyguesStore'
import { getAPIValidationError } from '~/scopes/checkout/utils/getAPIValidationError'
import ReassuranceItems from '~/scopes/reassurance/components/ReassuranceItems/ReassuranceItems.vue'

import BouyguesAssistanceDrawer from '../BouyguesAssistanceDrawer/BouyguesAssistanceDrawer.vue'
import PriceOption from '../PriceOption/PriceOption.vue'
import TermsAndConditionsCheckbox from '../TermsAndConditionsCheckbox/TermsAndConditionsCheckbox.vue'

import translations from './PhonelineSetupForm.translations'
import { validatePhoneNumber } from './validators/validatePhoneNumber'

defineProps<{
  keepPhoneNumberPriceOption: PriceOptionType
  newPhoneNumberPriceOption: PriceOptionType
}>()

const { openErrorToast } = useTheToast()

const user = useUser()
const i18n = useI18n()
const router = useRouter()
const { setPhonelineSetupInformation } = useBouyguesStore()

const isLoading = ref(false)
const apiErrors = ref<{
  rioCode?: string
  iban?: string
  phoneNumber?: string
}>({})

type Values = {
  option: string
  rioCode: string
  iban: string
  phoneNumber: string
  childrenOnlineProtection: boolean
  termsAndConditions: boolean
}

const initialValues = {
  option: 'KEEP_PHONE_NUMBER',
  rioCode: '',
  iban: '',
  phoneNumber:
    user.phone.dial && user.phone.phone
      ? `${user.phone.dial}${user.phone.phone}`
      : '',
  childrenOnlineProtection: false,
  termsAndConditions: false,
}

const IBANRegex = /^[A-Z]{2}(?:[ ]?[0-Z]){25}$/

const validate = makeValidate<Values>({
  option: required(i18n(translations.inputRequired)),
  iban: [
    required(i18n(translations.inputRequired)),
    matchingRegExp(IBANRegex, i18n(translations.IBANInputErrorInvalid)),
  ],
  phoneNumber: (value, values, ctx) => {
    if (values.option === 'NEW_PHONE_NUMBER') return FORM_VALID

    return validatePhoneNumber(
      i18n(translations.inputRequired),
      i18n(translations.phoneNumberInputErrorInvalid),
    )(value, values, ctx)
  },
  rioCode: (value, values, ctx) => {
    if (values.option === 'NEW_PHONE_NUMBER') return FORM_VALID

    return required(i18n(translations.inputRequired))(value, values, ctx)
  },
  termsAndConditions: (value) =>
    value === true
      ? FORM_VALID
      : i18n(translations.inputTermsAndConditionRequired),
})

async function handleSubmit(values: Values) {
  isLoading.value = true

  try {
    const { eligible: isEligible } = await $httpFetch(
      checkMobilePlanCustomerAccountEligibility,
      {
        body: {
          partner: 'BOUYGUES_TELECOM',
          iban: values.iban,
          rioCode: values.rioCode,
          phoneNumber: values.phoneNumber,
        },
      },
    )

    if (!isEligible) {
      router.push({ name: CHECKOUT.SERVICES.BOUYGUES.NOT_ELIGIBLE })

      return
    }

    setPhonelineSetupInformation({
      iban: values.iban,
      rioCode: values.rioCode,
      phoneNumber: values.phoneNumber,
      childrenOnlineProtection: values.childrenOnlineProtection,
    })
    router.push({ name: CHECKOUT.PAYMENT })
  } catch (err) {
    const httpApiError = err as HttpApiError

    if (httpApiError.status === 400) {
      apiErrors.value.phoneNumber = getAPIValidationError<Values>(
        httpApiError.errors,
        'phoneNumber',
        '/errors/invalid-format',
        i18n(translations.phoneNumberInputErrorInvalid),
      )
      apiErrors.value.rioCode = getAPIValidationError<Values>(
        httpApiError.errors,
        'rioCode',
        '/errors/invalid-format',
        i18n(translations.RIOInputErrorInvalid),
      )
      const ibanFormatApiError = getAPIValidationError<Values>(
        httpApiError.errors,
        'iban',
        '/errors/invalid-parameter',
        i18n(translations.IBANInputErrorInvalid),
      )
      const ibanBankAccountNotAllowedApiError = getAPIValidationError<Values>(
        httpApiError.errors,
        'iban',
        '/errors/bank-account-type-not-allowed',
        i18n(translations.IBANInputErrorAccountNotAllowed),
      )
      apiErrors.value.iban =
        ibanFormatApiError || ibanBankAccountNotAllowedApiError
    }

    openErrorToast()
  } finally {
    isLoading.value = false
  }
}
</script>
